import { useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { pathForView } from '@aithora/shared';
import { useConsoleRole } from '../../session/SessionContext';
import { CONSOLE_INDEX } from '../../content/consoleIndex';
import { CONSOLE_FAQ } from '../../content/consoleFaq';
import { helpTab, sx } from '../../ui/designStyles';
import { hdVals, type HelpTab } from './helpVals';
import { askInHelpDrawer } from './askInHelpDrawer';
import './help.css';
import { BuildTagged } from '../../ui/buildLabels';
import { bannerCopyFor, guideFor } from '../../shell/FirstTimeBanner';

/**
 * Help (Full docs), ported from the design (T8538-8623, hdVals L33605). Hidden from the
 * side nav; reached from the ? in the top bar and the Help drawer's menu. The index on the
 * left lists only the entries written for the signed-in role, the box on top filters it as
 * you type, and Enter hands the question to the Help drawer, which answers it through
 * POST /help/ask like any other question. The URL may pick the tab (?tab=faq) or an entry
 * (?doc=Overview); the role never comes from it.
 */
const S = {
  root: 'display:flex;flex-direction:column;gap:12px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;',
  headRow: 'flex:0 0 auto;display:flex;align-items:baseline;gap:6px 14px;flex-wrap:wrap;min-width:0;',
  h1: 'margin:0;font-size:26px;font-weight:800;letter-spacing:-.025em;white-space:nowrap;',
  count: 'font-size:16px;color:#54606C;',
  role: 'display:inline-flex;align-items:center;gap:7px;font-size:14px;color:#3C454F;white-space:nowrap;',
  dot: 'width:7px;height:7px;border-radius:50%;background:#2F5C97;',
  // The welcome banner's content, kept here for good once the banner stops (V7 FOR YOUR ROLE card).
  roleCard: 'flex:0 0 auto;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);padding:12px 2px;min-width:0;',
  roleTag: 'display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:#fff;background:#2F5C97;',
  roleTitle: 'font-size:16px;font-weight:700;color:#22272E;',
  roleNote: 'flex:1 1 220px;min-width:0;font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;',
  roleStart: 'display:inline-flex;align-items:center;min-height:28px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;',
  searchRow: 'flex:0 0 auto;position:relative;display:flex;align-items:center;min-width:0;',
  searchIcon: 'position:absolute;left:13px;top:50%;margin-top:-8px;pointer-events:none;',
  input: "box-sizing:border-box;flex:1 1 auto;min-width:0;height:42px;padding:0 118px 0 38px;border-radius:10px;border:1px solid rgba(34,39,46,.18);background:#FCFAF4;font-family:'Archivo',system-ui,sans-serif;font-size:16px;color:#22272E;outline:none;",
  enter: 'position:absolute;right:12px;top:50%;transform:translateY(-50%);font-size:14px;color:#54606C;white-space:nowrap;pointer-events:none;',
  tabs: 'flex:0 0 auto;display:flex;align-items:center;gap:22px;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);',
  listCount: 'font-size:14px;color:#54606C;padding-bottom:9px;white-space:nowrap;',
  grid: 'flex:1 1 auto;min-height:0;display:grid;grid-template-columns:minmax(190px,240px) minmax(0,1fr);min-width:0;',
  list: 'min-height:0;overflow-y:auto;overflow-x:hidden;border-right:1px solid rgba(34,39,46,.10);padding:4px 12px 12px 0;display:flex;flex-direction:column;gap:12px;',
  empty: 'font-size:16px;color:#54606C;line-height:1.5;padding:8px 2px;',
  group: 'display:flex;flex-direction:column;gap:1px;',
  groupLabel: 'font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;padding:4px 10px 5px;',
  row: (on: boolean) =>
    'display:block;padding:7px 10px;border-radius:7px;text-decoration:none;' +
    (on ? 'background:rgba(47,92,151,.10);color:#264C7E;box-shadow:inset 2px 0 0 #2F5C97;' : 'color:#22272E;'),
  rowIn: 'display:flex;align-items:center;gap:8px;min-width:0;',
  rowTitle: 'flex:1 1 auto;min-width:0;font-size:16px;font-weight:600;line-height:1.35;',
  rowTag: 'flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.05em;color:#54606C;',
  detail: 'min-height:0;overflow-y:auto;overflow-x:hidden;padding:6px 4px 16px 28px;',
  doc: 'display:flex;flex-direction:column;gap:18px;max-width:760px;animation:riseIn .26s cubic-bezier(.22,.61,.36,1) backwards;',
  docHead: 'display:flex;flex-direction:column;gap:7px;',
  eyebrow: 'font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;',
  titleRow: 'display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;',
  h2: 'margin:0;font-size:22px;font-weight:800;letter-spacing:-.02em;line-height:1.2;',
  go: 'display:inline-flex;align-items:center;gap:6px;height:30px;padding:0 12px;border-radius:8px;background:#2F5C97;color:#FFFFFF;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;',
  purpose: 'font-size:16px;line-height:1.6;color:#22272E;text-wrap:pretty;',
  meta: 'font-size:14px;color:#54606C;',
  section: 'display:flex;flex-direction:column;gap:0;',
  secLabel: 'font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#3C454F;font-weight:700;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.12);',
  kvRow: 'display:flex;flex-wrap:wrap;gap:3px 16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.07);align-items:baseline;',
  kvKey: (mono: boolean) => 'flex:0 0 150px;min-width:0;font-size:16px;font-weight:700;color:' + (mono ? '#2F5C97' : '#3C454F') + ';line-height:1.45;' + (mono ? 'letter-spacing:.04em;' : ''),
  liRow: 'display:flex;flex-wrap:nowrap;gap:8px;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.07);align-items:baseline;',
  liKey: 'flex:0 0 18px;font-size:16px;font-weight:800;color:#2F5C97;font-variant-numeric:tabular-nums;',
  rowV: 'flex:1 1 220px;font-size:16px;line-height:1.55;color:#22272E;text-wrap:pretty;min-width:0;',
  seeRow: 'display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;',
  seeLink: 'font-size:16px;font-weight:600;color:#2F5C97;text-decoration:none;border-bottom:1px solid rgba(47,92,151,.3);',
  faq: 'display:flex;flex-direction:column;gap:10px;max-width:720px;animation:riseIn .26s cubic-bezier(.22,.61,.36,1) backwards;',
  faqQ: 'margin:0;font-size:21px;font-weight:800;letter-spacing:-.02em;line-height:1.25;text-wrap:pretty;',
  faqA: 'font-size:16px;line-height:1.65;color:#22272E;text-wrap:pretty;',
  closing: 'font-size:14px;color:#54606C;line-height:1.5;margin-top:6px;',
};

export function HelpPage() {
  const { role } = useConsoleRole();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: HelpTab = params.get('tab') === 'faq' ? 'faq' : 'guide';
  const [q, setQ] = useState('');
  const [doc, setDoc] = useState<string | null>(params.get('doc'));
  const [faq, setFaq] = useState<string | null>(null);

  const v = useMemo(() => hdVals(CONSOLE_INDEX, CONSOLE_FAQ, role, { tab, q, doc, faq }), [role, tab, q, doc, faq]);
  // What the welcome banner offered, always here (Andrew, 2026-10-19).
  const welcome = bannerCopyFor(role);

  const setTab = (t: HelpTab) => {
    const next = new URLSearchParams(params);
    if (t === 'faq') next.set('tab', 'faq');
    else next.delete('tab');
    setParams(next, { replace: true });
  };
  const onKey = (ev: ReactKeyboardEvent<HTMLInputElement>) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    const text = q.trim();
    if (!text) return;
    setQ('');
    askInHelpDrawer(text);
  };

  return (
    <div data-screen-label="Help" style={sx(S.root)} className="hd">
      <div style={sx(S.headRow)}>
        <h1 style={sx(S.h1)}>Help</h1>
        <span style={sx(S.count)}>{v.count}</span>
        <span style={sx('flex:1 1 20px;')} />
        <span style={sx(S.role)}>
          <span style={sx(S.dot)} />
          {`Showing help for ${v.roleName}`}
        </span>
      </div>
      {welcome && (
        <div style={sx(S.roleCard)} data-testid="help-for-your-role">
          <span style={sx(S.roleTag)}>FOR YOUR ROLE</span>
          <span style={sx(S.roleTitle)}>{welcome.title}</span>
          <span style={sx(S.roleNote)}>{welcome.note}</span>
          <Link to={guideFor(role)} style={sx(S.roleStart)}>
            Start
          </Link>
        </div>
      )}
      <div style={sx(S.searchRow)}>
        <svg viewBox="0 0 20 20" width="16" height="16" style={sx(S.searchIcon)} fill="none" stroke="#54606C" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="M13 13l4 4" />
        </svg>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask a question, or type to filter the pages"
          aria-label="Ask a question, or type to filter the pages"
          style={sx(S.input)}
        />
        <span style={sx(S.enter)}>Enter to ask {'↵'}</span>
      </div>
      <div style={sx(S.tabs)} role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'guide'} className="dlink" style={sx(helpTab(tab === 'guide'))} onClick={() => setTab('guide')}>
          Find a page
        </button>
        <button type="button" role="tab" aria-selected={tab === 'faq'} className="dlink" style={sx(helpTab(tab === 'faq'))} onClick={() => setTab('faq')}>
          FAQ
        </button>
        <span style={sx('flex:1 1 20px;')} />
        <span style={sx(S.listCount)}>{v.listCount}</span>
      </div>
      <div style={sx(S.grid)}>
        <nav style={sx(S.list)} aria-label={tab === 'guide' ? 'Pages' : 'Questions'}>
          {v.groups.length === 0 && <div style={sx(S.empty)}>Nothing here matches. Press Enter and the Help assistant will answer it.</div>}
          {v.groups.map((g) => (
            <div key={g.label} style={sx(S.group)}>
              <span style={sx(S.groupLabel)}>{g.label}</span>
              {g.items.map((i) => (
                <button
                  key={i.title}
                  type="button"
                  className="dlink hd-row"
                  aria-current={i.on ? 'true' : undefined}
                  style={sx(S.row(i.on))}
                  onClick={() => (tab === 'guide' ? setDoc(i.title) : setFaq(i.title))}
                >
                  <span style={sx(S.rowIn)}>
                    <span style={sx(S.rowTitle)}>{i.title}</span>
                    {i.tag && <BuildTagged tag={i.tag}><span style={sx(S.rowTag)}>{i.tag}</span></BuildTagged>}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div style={sx(S.detail)}>
          {v.entry && (
            <div key={v.entry.t} style={sx(S.doc)}>
              <div style={sx(S.docHead)}>
                <span style={sx(S.eyebrow)}>{v.entry.g}</span>
                <div style={sx(S.titleRow)}>
                  <h2 style={sx(S.h2)}>{v.entry.t}</h2>
                  {v.goView && (
                    <button type="button" className="dlink hx-go" style={sx(S.go)} onClick={() => navigate(pathForView(v.goView!))}>
                      <span>{v.goLabel}</span> {'→'}
                    </button>
                  )}
                </div>
                <div style={sx(S.purpose)}>{v.entry.purpose}</div>
                <span style={sx(S.meta)}>{v.meta}</span>
              </div>
              {v.sections.map((sec) => (
                <div key={sec.label} style={sx(S.section)}>
                  <span style={sx(S.secLabel)}>{sec.label}</span>
                  {sec.rows.map((r, n) => (
                    <div key={n} style={sx(r.kind === 'kv' ? S.kvRow : S.liRow)}>
                      <span style={sx(r.kind === 'kv' ? S.kvKey(!!r.mono) : S.liKey)}>{r.k}</span>
                      <span style={sx(S.rowV)}>{r.v}</span>
                    </div>
                  ))}
                </div>
              ))}
              {v.see.length > 0 && (
                <div style={sx(S.seeRow)}>
                  <span style={sx(S.eyebrow)}>See also</span>
                  {v.see.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="dlink hd-see"
                      style={sx(S.seeLink)}
                      onClick={() => {
                        setDoc(t);
                        setQ('');
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {v.faq && (
            <div key={v.faq.q} style={sx(S.faq)}>
              <span style={sx(S.eyebrow)}>{v.faq.g}</span>
              <h2 style={sx(S.faqQ)}>{v.faq.q}</h2>
              <div style={sx(S.faqA)}>{v.faq.a}</div>
              <div style={sx(S.closing)}>{v.closing}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
